import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import PaymentIcon from '@mui/icons-material/Payment';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ApiIcon from '@mui/icons-material/Code';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import * as amplitude from '@amplitude/analytics-browser';
import { useUserData } from '../hooks/useUserData';
import { canAccessAIAgents } from '../planAccess';
import { isAdminEmail } from '../adminConfig';

const drawerWidth = 240;


const Sidebar = ({ user, mobileOpen, handleDrawerClose }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();
  const location = useLocation();

  const { userData, loadingUserData } = useUserData();
  const hasAIAgentsAccess = canAccessAIAgents(userData?.plan);
  const showAIAgentsComingSoonBadge = !loadingUserData && !hasAIAgentsAccess;
  const showAdminLink = isAdminEmail(user?.email);

  const handleBuyClick = () => {
    setLoading(false);
    navigate('/pricing');
    amplitude.track('Buy Time Clicked from Header', { userId: user?.uid });
  };

  const handleNavigationClick = (destination) => {
    amplitude.track('Sidebar Navigation Clicked', { destination, userId: auth.currentUser?.uid });
    handleDrawerClose();
  };
  
  const handleProfileClick = () => {
    navigate('/settings');
    amplitude.track('Profile Section Clicked from Sidebar', { userId: user?.uid });
    handleDrawerClose();
  };

  const isActive = (path) => {
    if (path === '/ai-analysis') {
      return location.pathname === '/ai-analysis' || location.pathname.startsWith('/ai-analysis/');
    }
    if (path === '/ai-agents') {
      return location.pathname === '/ai-agents' || location.pathname.startsWith('/ai-agents/');
    }
    return location.pathname === path;
  };

  const drawerContent = (
    <Box display="flex" flexDirection="column" height="100%">
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Link to="/new" onClick={() => handleNavigationClick('/new')}>
            <Box
              component="img"
              src="/logo/xosumAM_logo_transparent-min.png"
              alt="Xosum.AM Logo"
              sx={{ height: 50, width: 'auto', verticalAlign: 'middle' }}
            />
        </Link>
      </Box>
      <Divider/>

      <List>
        <ListItem
          button
          component={Link}
          to="/new"
          sx={{
            color: 'purple',
            '&:hover': { backgroundColor: '#e0e0e0' },
            backgroundColor: isActive('/new') ? '#f0e4ff' : 'transparent'
          }}
          onClick={() => handleNavigationClick('/new')}
        >
          <ListItemIcon><MicIcon /></ListItemIcon>
          <ListItemText primary="Խոսքից տեքստ" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/text-to-speech"
          sx={{
            color: 'purple',
            '&:hover': { backgroundColor: '#e0e0e0' },
            backgroundColor: isActive('/text-to-speech') ? '#f0e4ff' : 'transparent'
          }}
          onClick={() => handleNavigationClick('/text-to-speech')}
        >
          <ListItemIcon><RecordVoiceOverIcon /></ListItemIcon>
          <ListItemText primary="Տեքստից խոսք" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/history"
          sx={{
            color: 'purple',
            '&:hover': { backgroundColor: '#e0e0e0' },
            backgroundColor: isActive('/history') ? '#f0e4ff' : 'transparent'
          }}
          onClick={() => handleNavigationClick('/history')}
        >
          <ListItemIcon><HistoryIcon /></ListItemIcon>
          <ListItemText primary="Պատմություն" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/ai-analysis"
          sx={{
            color: 'purple',
            '&:hover': { backgroundColor: '#e0e0e0' },
            backgroundColor: isActive('/ai-analysis') ? '#f0e4ff' : 'transparent'
          }}
          onClick={() => handleNavigationClick('/ai-analysis')}
        >
          <ListItemIcon><PlaylistAddCheckIcon /></ListItemIcon>
          <ListItemText primary="AI Վերլուծություն" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/ai-agents"
          sx={{
            color: 'purple',
            '&:hover': { backgroundColor: '#e0e0e0' },
            backgroundColor: isActive('/ai-agents') ? '#f0e4ff' : 'transparent'
          }}
          onClick={() => handleNavigationClick('/ai-agents')}
        >
          <ListItemIcon><SmartToyIcon /></ListItemIcon>
          <ListItemText
            primary={showAIAgentsComingSoonBadge ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography component="span" sx={{ fontSize: '0.95rem' }}>
                  AI Ագենտներ
                </Typography>
                <Chip
                  label="Շուտով"
                  size="small"
                  sx={{
                    height: 20,
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    backgroundColor: '#fff3cd',
                    color: '#7a4e00',
                  }}
                />
              </Box>
            ) : 'AI Ագենտներ'}
          />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/api"
          sx={{
            color: 'purple',
            '&:hover': { backgroundColor: '#e0e0e0' },
            backgroundColor: isActive('/api') ? '#f0e4ff' : 'transparent'
          }}
          onClick={() => handleNavigationClick('/api')}
        >
          <ListItemIcon><ApiIcon /></ListItemIcon>
          <ListItemText primary="API" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/reports"
          sx={{
            color: 'purple',
            '&:hover': { backgroundColor: '#e0e0e0' },
            backgroundColor: isActive('/reports') ? '#f0e4ff' : 'transparent'
          }}
          onClick={() => handleNavigationClick('/reports')}
        >
          <ListItemIcon><AssessmentIcon /></ListItemIcon>
          <ListItemText primary="Վիճակագրություն" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/settings"
          sx={{
            color: 'purple',
            '&:hover': { backgroundColor: '#e0e0e0' },
            backgroundColor: isActive('/settings') ? '#f0e4ff' : 'transparent'
          }}
          onClick={() => handleNavigationClick('/settings')}
        >
          <ListItemIcon><SettingsIcon /></ListItemIcon>
          <ListItemText primary="Կարգավորումներ" />
        </ListItem>
        {showAdminLink && (
          <ListItem
            button
            component={Link}
            to="/admin"
            sx={{
              color: '#b71c1c',
              '&:hover': { backgroundColor: '#fde7e7' },
              backgroundColor: isActive('/admin') ? '#fde7e7' : 'transparent',
            }}
            onClick={() => handleNavigationClick('/admin')}
          >
            <ListItemIcon sx={{ color: '#b71c1c' }}><AdminPanelSettingsIcon /></ListItemIcon>
            <ListItemText primary="Admin" />
          </ListItem>
        )}
      </List>

      <Box sx={{ mt: 'auto' }}>
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ p: 2 }}>
            {user && (
                <Tooltip title="Կարգավորումներ" arrow>
                    <Box 
                        onClick={handleProfileClick} 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            p: 1.5,
                            borderRadius: 2, 
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            '&:hover': { backgroundColor: 'action.hover' }
                        }}
                    >
                        <Avatar
                            src={user.photoURL || undefined}
                            alt="User Profile"
                            sx={{
                                width: 40,
                                height: 40,
                                mr: 1.5,
                            }}
                        >
                            {!user.photoURL && (user?.displayName?.[0] || user?.email?.[0] || '').toUpperCase()}
                        </Avatar>
                        <Box sx={{ textAlign: 'left', overflow: 'hidden' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.3, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                {user.displayName || 'Օգտատեր'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                {user.email}
                            </Typography>
                        </Box>
                    </Box>
                </Tooltip>
            )}

            {!loadingUserData && (!userData?.plan || userData.plan === 'free') && (
                <Box sx={{ mt: user ? 2 : 0, textAlign: 'center' }}>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <PaymentIcon />}
                    sx={{
                    width: '100%',
                    color: '#fff',
                    backgroundColor: '#ff5722',
                    paddingY: 1.5,
                    '&:hover': { backgroundColor: '#e64a19' },
                    }}
                    onClick={handleBuyClick}
                    disabled={loading}
                >
                    Բաժանորդագրվել
                </Button>
                </Box>
            )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
